import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  HostBinding,
  inject,
  OnDestroy,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterModule } from '@angular/router';
import { MenuItem } from '@components/abb-actions-menu/abb-actions-menu.component';
import { AbbIconComponent } from '@components/abb-icon/abb-icon.component';
import {
  AbbTableComponent,
  IColumn,
} from '@components/abb-table/abb-table.component';
import { PAGES } from '@constants/constants';
import { OverflowTooltipDirective } from '@directives/overflow-tooltip.directive';
import { deleteGroupMutation, groupListQuery } from '@graphql/asset-management';
import { TranslateService } from '@ngx-translate/core';
import { ASSET_MANAGEMENT_SUBTITLE } from '@pages/asset-management/asset-management';
import { GroupsSidePanelComponent } from '@pages/asset-management/groups-side-panel/groups-side-panel.component';
import { NumberPipe } from '@pipes/number.pipe';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import { OverlayService } from '@services/ui/overlay.service';
import { ToastService } from '@services/ui/toast.service';
import { PermissionsService } from '@services/user/permissions.service';
import { dataProviderFromObservable } from '@utils/data-provider';
import { getErrorMessage } from '@utils/errors';
import { LOADING } from '@utils/loader';
import { t } from '@utils/strings';
import { Apollo } from 'apollo-angular';
import { GroupListQuery, GroupListQueryVariables } from 'gql/graphql';
import { BehaviorSubject, first, Subscription } from 'rxjs';

@Component({
  selector: 'abb-groups',
  standalone: true,
  imports: [
    CommonModule,
    AbbTableComponent,
    TPipe,
    RouterModule,
    NumberPipe,
    AbbIconComponent,
    OverflowTooltipDirective,
    MatProgressSpinnerModule,
  ],
  template: `
    <abb-table
      [loading]="loading()"
      [columns]="columns()"
      [dataProvider]="dataProvider"
      [defaultFilters]="{}"
      [filters]="[]"
      [defaultSortBy]="[]"
      [actions]="actions"
      [expandable]="true"
      [expandedTemplateRef]="groupDetailsTemplate() ?? null"
      (onExpand)="getGroupData($event)"
      [itemCompareFn]="compareFn"
    ></abb-table>
    <ng-template #groupDetails let-item>
      <div class="text-abb-font/70 py-3 pl-9">
        @if (expandedDeviceData(); as expandedData) {

        <div class="grid grid-cols-[25%_25%_1fr] gap-y-4 gap-x-2">
          @for (asset of expandedData.assets; track $index) {
          <a
            [routerLink]="
              asset.__typename === 'Device'
                ? ['..', 'devices', asset.id]
                : ['/properties', asset.id]
            "
            overflowTooltip
            class="block truncate text-abb-primary/80 hover:text-abb-primary transition-colors cursor-pointer"
            >{{ asset.name }}</a
          >
          <ng-container
            *ngTemplateOutlet="consumption; context: { $implicit: asset }"
          ></ng-container>
          <div overflowTooltip class="block truncate">
            {{ asset.__typename === 'Device' ? asset.property.name : '-' }}
          </div>
          }
        </div>
        } @else {
        <div class="flex items-center justify-center col-span-full">
          <mat-progress-spinner
            [diameter]="40"
            color="primary"
            mode="indeterminate"
          ></mat-progress-spinner>
        </div>
        }
      </div>
    </ng-template>
    <ng-template #name let-item>
      <span
        overflowTooltip
        class="block truncate text-abb-primary/80 font-medium"
        >{{ item.name }}</span
      >
    </ng-template>
    <ng-template #consumption let-item>
      <div class="flex gap-x-2 items-center">
        <abb-icon name="mat/bolt" class="text-abb-warning"></abb-icon>
        <span overflowTooltip class="block truncate font-medium"
          >{{ item.consumption | number }} W</span
        >
      </div>
    </ng-template>
    <ng-template #property let-item>
      <span class="block truncate" overflowTooltip>
        {{
          item.properties.length > 1
            ? ('Multiple properties' | t)
            : item.properties[0]?.name
        }}
      </span>
    </ng-template>
    <ng-template #description let-item>
      <div class="flex justify-between gap-x-2 items-center">
        <span class="truncate" overflowTooltip> {{ item.description }}</span>
      </div>
    </ng-template>
  `,
  styles: ``,
  providers: [
    { provide: TRANSLATION_PREFIX, useValue: 'asset_management.tabs.groups' },
  ],
})
export class GroupsComponent implements OnDestroy {
  private subscriptions: Subscription[] = [];
  @HostBinding('attr.id') componentid = 'groups-component';
  nameTemplate = viewChild<TemplateRef<unknown>>('name');
  consumptionTemplate = viewChild<TemplateRef<unknown>>('consumption');
  propertyTemplate = viewChild<TemplateRef<unknown>>('property');
  descriptionTemplate = viewChild<TemplateRef<unknown>>('description');
  groupDetailsTemplate = viewChild<TemplateRef<unknown>>('groupDetails');
  groups$ = new BehaviorSubject<GroupListQuery['groupList'] | []>([]);
  loading = signal(true);
  expandedDeviceData = signal<GroupListQuery['groupList'][number] | null>(null);
  subtitle = inject(ASSET_MANAGEMENT_SUBTITLE);
  prefix = inject(TRANSLATION_PREFIX);
  awaitingRes = inject(LOADING);
  canEdit = computed(() =>
    this.permissionsService.canEditPage(PAGES.ASSETMANAGEMENT)
  );
  compareFn = (
    a: GroupListQuery['groupList'][number] | null,
    b: GroupListQuery['groupList'][number] | null
  ) => (!a && !b) || a?.id === b?.id;
  dataProvider = dataProviderFromObservable<
    GroupListQuery['groupList'][number],
    object
  >(this.groups$, () => true);
  columns = computed<IColumn<GroupListQuery['groupList'][number]>[]>(() => {
    return [
      {
        id: 'name',
        name: 'Name',
        translate: true,
        templateRef: this.nameTemplate(),
      },
      {
        id: 'consumption',
        name: 'Consumption',
        templateRef: this.consumptionTemplate(),
        translate: true,
      },
      {
        id: 'properties',
        name: 'Property',
        templateRef: this.propertyTemplate(),
        translate: true,
      },
      {
        id: 'description',
        name: 'Description',
        templateRef: this.descriptionTemplate(),
        translate: true,
      },
      {
        name: 'actions',
        tableCellClass: this.canEdit() ? 'w-0' : 'hidden',
        tableHeaderCellClass: this.canEdit() ? 'w-0' : 'hidden',
      },
    ];
  });

  actions: Array<MenuItem<GroupListQuery['groupList'][number]>> = [
    { label: 'Edit Group', action: (groups) => this.openEditPanel(groups[0]) },
    {
      label: 'Remove Group',
      action: (groups) => this.openDeleteModal(groups[0]),
      // (users) => this.openDeleteModal(users![0]),
    },
  ];

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }
  constructor(
    private readonly apollo: Apollo,
    private readonly overlay: OverlayService,
    private readonly toast: ToastService,
    private readonly translate: TranslateService,
    private readonly permissionsService: PermissionsService
  ) {
    this.subscriptions.push(
      apollo
        .watchQuery<GroupListQuery, GroupListQueryVariables>({
          query: groupListQuery,
          variables: {
            includeConsumption: true
          }
        })
        .valueChanges.subscribe({
          next: ({ data, loading }) => {
            this.groups$.next(data.groupList);
            this.loading.set(loading);
            this.subtitle.set({
              key: 'You have a total of {tot} groups configured',
              params: {
                tot: data.groupList.length,
              },
            });
            this.expandedDeviceData.update((prev) => {
              if (prev) {
                return data.groupList.find((g) => g.id === prev.id) ?? null;
              }
              return null;
            });
          },
          error: (err) => {
            this.loading.set(false);
            //let errMsg = getErrorMessage(err, this.translate);
            //this.toast.notifyError(errMsg);
          },
        })
    );
  }

  getGroupData(el: GroupListQuery['groupList'][number] | null) {
    if (!el) {
      this.expandedDeviceData.set(null);
      return;
    }
    this.expandedDeviceData.set(el);
  }

  openEditPanel(item: GroupListQuery['groupList'][number]) {
    this.overlay.openSidePanel(GroupsSidePanelComponent, {
      mode: 'edit',
      defaultData: item,
    });
  }

  openDeleteModal(group: GroupListQuery['groupList'][number]) {
    this.overlay
      .openAlert({
        message: `${t(
          this.prefix,
          'Are you sure you want to remove group',
          this.translate
        )} <b>${group.name}</b>?`,
        htmlMessage: true,
        confirmText: t(this.prefix, 'Remove', this.translate),
      })
      .then(() => {
        this.awaitingRes.set(true);
        this.deleteGroup(group.id).finally(() => this.awaitingRes.set(false));
      })
      .catch(() => console.log('cancel'));
  }

  deleteGroup(id: string) {
    return new Promise<void>((resolve, reject) =>
      this.apollo
        .mutate({
          mutation: deleteGroupMutation,
          variables: { id },
        })
        .pipe(first())
        .subscribe({
          next: () => {
            resolve();
            this.toast.notifySuccess(
              t(this.prefix, 'Group removed successfully', this.translate)
            );
            this.groups$.next(this.groups$.value.filter((g) => g.id !== id));
          },
          error: (err) => {
            reject(new Error(err));
            let errMsg = getErrorMessage(err, this.translate);
            this.toast.notifyError(errMsg);
          },
        })
    );
  }
}
